/* @flow */
import * as React from 'react';
import { computed, observable } from 'mobx';
import { observer, inject } from 'mobx-react';
import type { TranslatorProps } from 'react-i18next';
import { translate } from 'react-i18next';

import Loading from 'components/Loading/Loading';
import AutoCompleteModel from 'models/forms/AutoCompleteModel';
import type { DataSource } from 'models/CustomerAccounts';
import PageWrapper from 'components/PageWrapper/PageWrapper';
import FormWrapper from 'components/FormWrapper/FormWrapper';
import FormSection from 'components/FormSection/FormSection';
import { autoCompleteErCompanies } from 'api/forms';
import { ComponentWithInjectedProps } from 'utils/injectedProps';
import CustomerAccountStore from 'stores/CustomerAccountStore';

import ElectroTextField from '../components/inputs/ElectroTextField/ElectroTextField';
import ElectroSelect from '../components/inputs/ElectroSelect/ElectroSelect';
import ElectroSelectExtra from '../components/inputs/ElectroSelectExtra/ElectroSelectExtra';
import ElectroResponseMessage from '../components/inputs/ElectroResponseMessage';
import ElectroFileTransfer from '../components/inputs/ElectroFileTransfer';
import FormControls from '../components/controls/FormControls/FormControls';

import { makeFields, customerAccountCreate, customerAccountEdit } from './setupCustomerAccountForm';

export type CustomerAccountFormProps = {
  mode: 'edit' | 'create',
  customerAccountId?: string,
  companyCode?: string,
  dataSource?: DataSource,
};

type InjectedProps = {
  customerAccountStore: CustomerAccountStore,
} & TranslatorProps;

@translate()
@inject('customerAccountStore', 'actionDrawerStore')
@observer
export default class BrandForm extends ComponentWithInjectedProps<
  CustomerAccountFormProps,
  {},
  InjectedProps
> {
  @observable erCompaniesModel = new AutoCompleteModel(autoCompleteErCompanies);

  @observable localState = {
    responseMessage: '',
    showResponseMessage: false,
    isLoading: false,
    firstLoad: true,
    hasSuccess: false,
    hasError: false,

    // customer_code
    selectedErCompany: undefined,
  };

  @computed get form(): Object {
    const { mode, t, customerAccountStore, dataSource } = this.props;

    const fields = makeFields(t, mode, dataSource, customerAccountStore.selectedCustomerAccount);

    switch (mode) {
      case 'edit':
        return customerAccountEdit(
          fields,
          customerAccountStore.selectedCustomerAccount || {},
          this.localState,
          this.props.t
        );

      case 'create':
      default:
        return customerAccountCreate(fields, this.localState, this.props.t);
    }
  }

  constructor(props: CustomerAccountFormProps & InjectedProps) {
    super(props);

    const { customerAccountStore, customerAccountId, companyCode, mode, dataSource } = props;

    if (mode === 'edit') {
      if (!customerAccountId) {
        throw new Error('Customer Account ID is required for Customer Account edit form');
      }

      if (!dataSource) {
        throw new Error('Data Source/Type is required for Customer Account edit form');
      }

      if (
        !customerAccountStore.selectedCustomerAccount ||
        customerAccountStore.selectedCustomerAccount.id !== customerAccountId ||
        customerAccountStore.selectedCustomerAccount.code !== companyCode
      ) {
        this.localState.isLoading = true;

        customerAccountStore
          .loadCustomerAccount(dataSource, customerAccountId, companyCode)
          .then(customerAccount => {
            if (customerAccount) {
              this.localState.selectedErCompany = customerAccount.company_code;
            }

            this.localState.isLoading = false;
          })
          .catch(err => {
            this.localState.showResponseMessage = true;
            this.localState.hasError = true;
            this.localState.responseMessage = err.message;
            this.localState.isLoading = false;
          });
      }
    }
  }

  render() {
    const { mode, t } = this.props;

    return this.localState.isLoading && this.localState.firstLoad ? (
      <Loading />
    ) : (
      <PageWrapper isFlex={false}>
        <ElectroResponseMessage localState={this.localState} />
        <FormWrapper form={this.form}>
          <FormSection form={this.form} section="details">
            <ElectroTextField field={this.form.$('details.id')} />
            <ElectroTextField field={this.form.$('details.name')} />

            <ElectroSelect
              form={this.form}
              formName="details"
              field={this.form.$('details.dataSource')}
              disabled={mode === 'edit'}
              valueKey="id"
            />
          </FormSection>

          <FormSection form={this.form} section="erCompanies">
            <ElectroSelect
              field={this.form.$('erCompanies.companyCode')}
              labelKey="label"
              valueKey="er_code"
              simpleValue
              model={this.erCompaniesModel}
            />
          </FormSection>
          <FormControls
            form={this.form}
            buttonText={
              this.props.mode === 'edit' ? t('general:actions.save') : t('general:actions.create')
            }
          />
        </FormWrapper>
      </PageWrapper>
    );
  }
}
